'use client';
import React, { Suspense, useState, useMemo } from 'react';
import { SectionTitle } from '@benzinga/core-ui';
import type { AnalystRatingsSummary, Ratings } from '@benzinga/calendar-manager';
import type { BullBearStatements } from '@benzinga/quotes-manager';
import type { News } from '@benzinga/basic-news-manager';

import type { BaseQuotePageProps, RatingsByMonthWithCounts } from '../../../../../src/utils/quote/types';
import type { ShortInterestSummary, InsiderTradesSummary } from '../../../../../src/utils/quote/summaries';
import type { OrganizeOptionDataReturn } from '@benzinga/data-option-chain';
import QuotePageTitleLink from '../_components/QuotePageTitleLink';
import { useQuoteTrackClick } from '../../../../../src/utils/quote/useQuoteTracking';
import { formatDate, isoToMillis, formatUpsidePercent } from '../../../../../src/components/Quote/formatters';
import { KPIBox, kpiToneFromBool } from '../../../../../src/components/Quote/KPIBox';
import {
  QuoteGatedTable,
  QuoteHeaderRow,
  QuoteTd,
  QuoteTh,
  QuoteThead,
  QuoteTr,
  useQuoteGatedRows,
} from '../../../../../src/components/Quote/QuoteDataTable';
import { FullDisplayBullVsBear } from '../../../../../src/components/Quote/BullVsBear';
import ShortInterestTrendChart from '../../../../../src/components/Quote/ShortInterestTrendChart';
import { QuoteFooter } from '../../../../../src/components/Quote/QuoteFooter';
import { QuoteNewsFeed } from '../../../../../src/components/Quote/QuoteNewsFeed';
import PeopleAlsoWatch from '../_components/PeopleAlsoWatch';

const FreestarAd = React.lazy(() => import('@benzinga/ads').then(module => ({ default: module.FreestarAd })));

interface SentimentContentProps extends BaseQuotePageProps {
  ratingsSummary: AnalystRatingsSummary | null;
  bullSayBearSay: BullBearStatements | null;
  ratingsByMonthWithCounts?: RatingsByMonthWithCounts[];
  shortInterestSummary: ShortInterestSummary | null;
  optionChainData?: OrganizeOptionDataReturn | null;
  insiderTradesSummary?: InsiderTradesSummary | null;
  sentimentNews: News[];
}

const isPositivePercent = (value: string | null): boolean | null => {
  if (!value) return null;
  return value.startsWith('+');
};

const putCallSignal = (
  calls: number,
  puts: number,
): { ratio: number; label: string; bullish: boolean | null } | null => {
  if (calls <= 0) return null;
  const ratio = puts / calls;
  return { ratio, label: ratio.toFixed(2), bullish: ratio < 1 ? true : ratio > 1 ? false : null };
};

const SentimentStatGrid: React.FC<{
  ratingsSummary: AnalystRatingsSummary | null;
  calls: number;
  puts: number;
  latestSI: number | null;
  priorSI: number | null;
  insiderNet: number | null;
  currentPrice: number | null;
}> = ({ ratingsSummary, calls, puts, latestSI, priorSI, insiderNet, currentPrice }) => {
  const pc = putCallSignal(calls, puts);
  const pcRatio = pc?.label ?? null;
  const pcBullish = pc?.bullish ?? null;
  const siTrend =
    latestSI !== null && priorSI !== null ? (latestSI < priorSI ? true : latestSI > priorSI ? false : null) : null;

  const consensusPT = ratingsSummary?.consensusPriceTarget ?? null;
  const highestPT = ratingsSummary?.highestPriceTarget ?? null;
  const lowestPT = ratingsSummary?.lowestPriceTarget ?? null;
  const consensusUpside = formatUpsidePercent(consensusPT, currentPrice);
  const highestUpside = formatUpsidePercent(highestPT, currentPrice);
  const lowestUpside = formatUpsidePercent(lowestPT, currentPrice);
  const insiderValue =
    insiderNet === null ? '—' : insiderNet > 0 ? 'Net Buyer' : insiderNet < 0 ? 'Net Seller' : 'Neutral';
  const insiderTone =
    insiderNet === null ? undefined : insiderNet > 0 ? 'positive' : insiderNet < 0 ? 'negative' : 'neutral';

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      <KPIBox
        label="Analyst Consensus"
        value={ratingsSummary?.consensusRating ?? '—'}
        sub={ratingsSummary?.numUniqueRatings ? `${ratingsSummary.numUniqueRatings} analysts` : undefined}
      />

      <KPIBox
        label="Options Flow (P/C)"
        value={pcRatio ?? '—'}
        sub={
          pcRatio
            ? pcBullish === true
              ? 'More calls — Bullish'
              : pcBullish === false
                ? 'More puts — Bearish'
                : 'Balanced — Neutral'
            : undefined
        }
        subTone={kpiToneFromBool(pcBullish)}
        footer={
          calls > 0 || puts > 0
            ? `${calls.toLocaleString('en-US')} calls · ${puts.toLocaleString('en-US')} puts`
            : undefined
        }
      />

      <KPIBox
        label="Avg Consensus PT"
        value={consensusPT ? `$${Number(consensusPT).toFixed(0)}` : '—'}
        sub={consensusUpside ?? undefined}
        subTone={kpiToneFromBool(isPositivePercent(consensusUpside))}
      />

      <KPIBox
        label="Highest Price Target"
        value={highestPT ? `$${Number(highestPT).toFixed(0)}` : '—'}
        sub={highestUpside ?? undefined}
        subTone={kpiToneFromBool(isPositivePercent(highestUpside))}
        footer={ratingsSummary?.highestPriceRating?.firm}
      />

      <KPIBox
        label="Lowest Price Target"
        value={lowestPT ? `$${Number(lowestPT).toFixed(0)}` : '—'}
        sub={lowestUpside ?? undefined}
        subTone={kpiToneFromBool(isPositivePercent(lowestUpside))}
        footer={ratingsSummary?.lowestPriceRating?.firm}
      />

      <KPIBox
        label="Short Interest"
        value={latestSI !== null ? `${Number(latestSI).toFixed(1)}%` : '—'}
        sub={siTrend === true ? 'Declining — Bullish' : siTrend === false ? 'Rising — Bearish' : undefined}
        subTone={kpiToneFromBool(siTrend)}
      />

      <KPIBox label="Insider Activity (30d)" value={insiderValue} valueTone={insiderTone} />
    </div>
  );
};

type RatingFilter = 'all' | 'upgrades' | 'downgrades' | 'initiations';

const matchesFilter = (r: Ratings, filter: RatingFilter): boolean => {
  if (filter === 'all') return true;
  const a = r.action_company ?? '';
  if (filter === 'upgrades') return a === 'Upgrades';
  if (filter === 'downgrades') return a === 'Downgrades';
  if (filter === 'initiations') return a === 'Initiates Coverage On';
  return true;
};

const PTChangeVisual: React.FC<{ prior: string; current: string }> = ({ prior, current }) => {
  const p = Number(prior);
  const c = Number(current);
  if (!p || !c) return <span className="text-gray-400 text-xs">—</span>;
  const isRaise = c > p;
  const color = isRaise ? 'text-green-600' : c < p ? 'text-red-500' : 'text-gray-500';
  return (
    <span className={`text-xs font-semibold whitespace-nowrap ${color}`}>
      ${p.toFixed(0)} → ${c.toFixed(0)}
    </span>
  );
};

const AccuracyBar: React.FC<{ pct: string | null | undefined }> = ({ pct }) => {
  const val = pct != null && pct !== '' ? Math.round(Number(pct)) : null;
  if (val == null || Number.isNaN(val)) return <span className="text-gray-400 text-xs">—</span>;
  return (
    <div className="flex items-center gap-1.5 min-w-[72px]">
      <span className="text-xs font-semibold text-gray-700 w-8 text-right">{val}%</span>
      <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
        <div className="h-full rounded-full bg-green-500" style={{ width: `${Math.min(val, 100)}%` }} />
      </div>
    </div>
  );
};

const AnalystRatingsTable: React.FC<{
  ratings: Ratings[];
  currentPrice: number | null;
}> = ({ ratings, currentPrice }) => {
  const [filter, setFilter] = useState<RatingFilter>('all');
  const trackClick = useQuoteTrackClick();
  const upgrades = useMemo(() => ratings.filter(r => r.action_company === 'Upgrades'), [ratings]);
  const downgrades = useMemo(() => ratings.filter(r => r.action_company === 'Downgrades'), [ratings]);
  const initiations = useMemo(() => ratings.filter(r => r.action_company === 'Initiates Coverage On'), [ratings]);
  const filtered = useMemo(
    () =>
      ratings
        .filter(r => matchesFilter(r, filter))
        .sort((a, b) => isoToMillis(b.date) - isoToMillis(a.date))
        .slice(0, 25),
    [ratings, filter],
  );

  const { rowBlurred } = useQuoteGatedRows(filtered.length);
  const tabs: { key: RatingFilter; label: string; count: number }[] = [
    { key: 'all', label: 'All Ratings', count: ratings.length },
    { key: 'upgrades', label: 'Upgrades', count: upgrades.length },
    { key: 'downgrades', label: 'Downgrades', count: downgrades.length },
    { key: 'initiations', label: 'Initiations', count: initiations.length },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => {
              setFilter(tab.key);
              trackClick('sentiment_filter');
            }}
            className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
              filter === tab.key ? 'bg-bzblue-1000 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      <QuoteGatedTable gateType="analyst-ratings" rowCount={filtered.length}>
        <QuoteThead>
          <QuoteHeaderRow>
            <QuoteTh>Date</QuoteTh>
            <QuoteTh>Firm</QuoteTh>
            <QuoteTh>Analyst & Accuracy</QuoteTh>
            <QuoteTh align="right">Upside / Downside</QuoteTh>
            <QuoteTh>PT Change</QuoteTh>
            <QuoteTh>Action</QuoteTh>
            <QuoteTh>Rating</QuoteTh>
          </QuoteHeaderRow>
        </QuoteThead>
        <tbody>
          {filtered.map((r, index) => {
            const price = currentPrice ?? (r.quote?.lastTradePrice ? Number(r.quote.lastTradePrice) : null);
            const upside = formatUpsidePercent(r.pt_current, price);
            const upsidePositive = isPositivePercent(upside);
            const actionColor =
              r.action_company === 'Upgrades'
                ? 'bg-green-100 text-green-700'
                : r.action_company === 'Downgrades'
                  ? 'bg-red-100 text-red-700'
                  : r.action_company === 'Initiates Coverage On'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-500';
            const actionLabel =
              r.action_company === 'Upgrades'
                ? 'Upgrade'
                : r.action_company === 'Downgrades'
                  ? 'Downgrade'
                  : r.action_company === 'Initiates Coverage On'
                    ? 'Initiate'
                    : r.action_company === 'Maintains' || r.action_company === 'Reiterates'
                      ? 'Maintained'
                      : (r.action_company ?? '—');

            return (
              <QuoteTr key={`${r.date}-${r.analyst}-${index}`} index={index} blurred={rowBlurred(index)}>
                <QuoteTd className="text-xs text-gray-500">{formatDate(r.date)}</QuoteTd>
                <QuoteTd className="text-sm font-medium text-gray-800">{r.analyst || '—'}</QuoteTd>
                <QuoteTd>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs text-gray-700">{r.analyst_name || '—'}</span>
                    <AccuracyBar pct={r.ratings_accuracy?.overall_success_rate} />
                  </div>
                </QuoteTd>
                <QuoteTd align="right">
                  {upside ? (
                    <span className={`text-xs font-bold ${upsidePositive ? 'text-green-600' : 'text-red-500'}`}>
                      {upside}
                    </span>
                  ) : (
                    <span className="text-gray-400 text-xs">—</span>
                  )}
                </QuoteTd>
                <QuoteTd>
                  {r.pt_prior && r.pt_current ? (
                    <PTChangeVisual current={r.pt_current} prior={r.pt_prior} />
                  ) : r.pt_current ? (
                    <span className="text-xs text-gray-700">${Number(r.pt_current).toFixed(0)}</span>
                  ) : (
                    <span className="text-gray-400 text-xs">—</span>
                  )}
                </QuoteTd>
                <QuoteTd>
                  <span className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${actionColor}`}>
                    {actionLabel}
                  </span>
                </QuoteTd>
                <QuoteTd className="text-xs text-gray-700">
                  {r.rating_prior && r.rating_prior !== r.rating_current ? (
                    <span>
                      <span className="text-gray-400">{r.rating_prior}</span>
                      <span className="mx-1 text-gray-300">→</span>
                      <span className="font-semibold">{r.rating_current}</span>
                    </span>
                  ) : (
                    r.rating_current || '—'
                  )}
                </QuoteTd>
              </QuoteTr>
            );
          })}
        </tbody>
      </QuoteGatedTable>
    </div>
  );
};

const KeySignalRow: React.FC<{ label: string; signal: string; bullish: boolean | null }> = ({
  label,
  signal,
  bullish,
}) => {
  const arrowClass = bullish === true ? 'text-green-600' : bullish === false ? 'text-red-500' : 'text-gray-500';
  const arrow = bullish === true ? '▲' : bullish === false ? '▼' : '—';
  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-700">{label}</span>
      <span className={`text-sm font-semibold ${arrowClass}`}>
        {arrow} {signal}
      </span>
    </div>
  );
};

const KeySignalsTable: React.FC<{
  ratingsSummary: AnalystRatingsSummary | null;
  calls: number;
  puts: number;
  latestSI: number | null;
  priorSI: number | null;
  insiderNet: number | null;
}> = ({ ratingsSummary, calls, puts, latestSI, priorSI, insiderNet }) => {
  const consensusRating = ratingsSummary?.consensusRating ?? null;
  const isBullishConsensus =
    consensusRating === 'Strong Buy' || consensusRating === 'Buy'
      ? true
      : consensusRating === 'Sell' || consensusRating === 'Strong Sell'
        ? false
        : null;

  const isBullishSI =
    latestSI !== null && priorSI !== null ? (latestSI < priorSI ? true : latestSI > priorSI ? false : null) : null;

  const pc = putCallSignal(calls, puts);
  const isBullishPC = pc?.bullish ?? null;
  const isBullishInsider = insiderNet === null ? null : insiderNet > 0 ? true : insiderNet < 0 ? false : null;

  return (
    <div className="rounded border border-[#e9ecef] bg-white p-4">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Key Signals</h2>
      <KeySignalRow label="Analyst Consensus" signal={consensusRating ?? 'N/A'} bullish={isBullishConsensus} />
      <KeySignalRow
        label="Short Interest"
        signal={latestSI !== null ? `${Number(latestSI).toFixed(1)}% short float` : 'N/A'}
        bullish={isBullishSI}
      />
      <KeySignalRow label="Options Flow" signal={pc ? `${pc.label} P/C ratio` : 'N/A'} bullish={isBullishPC} />
      <KeySignalRow
        label="Insider Activity"
        signal={
          insiderNet === null
            ? 'N/A'
            : insiderNet > 0
              ? 'Net Buyer (30d)'
              : insiderNet < 0
                ? 'Net Seller (30d)'
                : 'Neutral (30d)'
        }
        bullish={isBullishInsider}
      />
    </div>
  );
};

const SentimentContent: React.FC<SentimentContentProps> = ({
  bullSayBearSay,
  insiderTradesSummary,
  metaProps,
  optionChainData,
  profile,
  ratingsSummary,
  sentimentNews,
  shortInterestSummary,
  symbol,
}) => {
  const currentPrice = profile.richQuoteData?.lastTradePrice ? Number(profile.richQuoteData.lastTradePrice) : null;
  let calls = 0,
    puts = 0;
  for (const chains of Object.values(optionChainData?.dataByExpiry ?? {})) {
    for (const chain of chains ?? []) {
      for (const c of chain.calls ?? []) calls += c.volume ?? 0;
      for (const p of chain.puts ?? []) puts += p.volume ?? 0;
    }
  }

  const shortInterestSorted = [...(shortInterestSummary?.shortInterest ?? [])]
    .filter(s => s.shortPercentOfFloat != null)
    .sort((a, b) => isoToMillis(b.recordDate) - isoToMillis(a.recordDate));
  const latestSI = shortInterestSorted[0]?.shortPercentOfFloat ?? null;
  const priorSI = shortInterestSorted[1]?.shortPercentOfFloat ?? null;
  const insider30d = insiderTradesSummary?.lastThirtyDaysFilingsSummary ?? null;
  const insiderNet = insider30d
    ? Math.abs(insider30d.totalSharesPurchasedValue) - Math.abs(insider30d.totalSharesSoldValue)
    : null;
  return (
    <>
      <div className="main-div">
        <div className="flex flex-col gap-4 lg:flex-row">
          <div className="flex flex-col gap-y-6 w-full lg:w-9/12">
            <div className="flex flex-col gap-2">
              <SectionTitle level={1} uppercase={false}>
                <QuotePageTitleLink title={metaProps.title} symbol={symbol} />
              </SectionTitle>
              <p className="section-description">{metaProps.description}</p>
            </div>

            <FullDisplayBullVsBear profile={profile} statements={bullSayBearSay ?? null} />

            <KeySignalsTable
              calls={calls}
              insiderNet={insiderNet}
              latestSI={latestSI}
              priorSI={priorSI}
              puts={puts}
              ratingsSummary={ratingsSummary}
            />

            <SentimentStatGrid
              calls={calls}
              currentPrice={currentPrice}
              insiderNet={insiderNet}
              latestSI={latestSI}
              priorSI={priorSI}
              puts={puts}
              ratingsSummary={ratingsSummary}
            />

            {(shortInterestSummary?.shortInterest?.length ?? 0) > 0 && (
              <ShortInterestTrendChart shortInterest={shortInterestSummary!.shortInterest} />
            )}

            {(ratingsSummary?.ratings?.length ?? 0) > 0 && (
              <div className="flex flex-col gap-3">
                <SectionTitle level={2} size="xl" uppercase={false}>
                  Recent Analyst Actions
                </SectionTitle>
                <AnalystRatingsTable currentPrice={currentPrice} ratings={ratingsSummary!.ratings} />
              </div>
            )}

            {sentimentNews.length > 0 && (
              <div className="flex flex-col gap-2">
                <SectionTitle level={2} size="xl" uppercase={false}>
                  Insider &amp; Market Activity News
                </SectionTitle>
                <QuoteNewsFeed
                  nodes={sentimentNews}
                  query={{ channels: ['62', 'Options'], pageSize: 6, tickers: symbol }}
                  seeMoreEnabled={true}
                  showLoadMoreButton={true}
                />
              </div>
            )}

            <Suspense fallback={<div />}>
              <FreestarAd className="w-[300px] min-w-[300px]" onlyMobile={true} placement="incontent" />
            </Suspense>

            <QuoteFooter activeTab="sentiment" />
          </div>

          <PeopleAlsoWatch profile={profile} symbol={symbol}>
            <Suspense fallback={<div />}>
              <FreestarAd className="w-[300px] min-w-[300px]" onlyDesktop={true} placement="sidebar_2" />
            </Suspense>
          </PeopleAlsoWatch>
        </div>
      </div>
    </>
  );
};

export default SentimentContent;
